/**
 * Rollable items generation rules.
 * Derives rollable items from glossary entries and computed character stats.
 */
import { GLOSSARY } from '~/generated/glossary.data';
import { getCoreStats } from '~/utils/glossaryStats';
import type { Skill } from '~/pages/CharacterSheet/types';

export interface RollItem {
  name: string;
  formula: string;
  pool: number;
  category: "Skill" | "Core Stat" | "Movement" | "Manual";
}

const CORE_STAT_ENTRIES = getCoreStats();

/**
 * Gets movement roll items (Full Movement and Half Movement).
 * Derives names and formulas from glossary entry for Movement Speed.
 */
export function getMovementRollItems(movement: number): RollItem[] {
  const movementEntry = GLOSSARY['Movement Speed'];
  if (!movementEntry) return [];

  const formula = movementEntry.metadata.formula || "Body + Reflex + 3";

  return [
    {
      name: "Half Movement",
      formula: `(${formula}) / 2`,
      pool: Math.floor(movement / 2),
      category: "Movement",
    },
    {
      name: "Full Movement",
      formula,
      pool: movement,
      category: "Movement",
    },
  ];
}

/**
 * Gets all rollable items based on character data.
 * Derives items from glossary entries for core stats, movement, and skills.
 */
export function getAllRollableItems(
  coreStats: Record<string, number>,
  movement: number,
  skills: Skill[]
): RollItem[] {
  const items: RollItem[] = [];

  // Core Stats - derived from glossary
  for (const entry of CORE_STAT_ENTRIES) {
    items.push({
      name: entry.id,
      formula: entry.id,
      pool: coreStats[entry.id] ?? 0,
      category: "Core Stat",
    });
  }

  // Movement - derived from glossary entries
  items.push(...getMovementRollItems(movement));

  // Skills - already derived from glossary via computeSkills
  items.push(...skills.map((s) => ({ ...s, category: "Skill" as const })));

  return items;
}
